import {
  UserSearchDocument,
  EmployerSearchDocument,
  FetchState,
} from "@/shared/types";
import {
  OpenSearchIndex,
  OpenSearchIndexRequest,
  OpenSearchPackagesResponse,
  OpenSearchIndexState,
  OpenSearchDomainDetail
} from "../types";
import * as UserService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import { useNotifierStore } from "@/shared/store/notifier";
import { defineStore } from "pinia";
import _ from "lodash";

export const initialState: OpenSearchIndexState = {
  fetchState: FetchState.Uninitialized,
  openSearchDomainDetails: null,
  domainDetailsFetchState: FetchState.Uninitialized,
  userSearchDocument: null,
  employerSearchDocument: null,
  openSearchIndex: null,
  parameterValue: null
};

export const useOpenSearchIndexStore = defineStore("openSearchIndex", {
    state: (): OpenSearchIndexState => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: state => state.fetchState === FetchState.Loaded,
        isDomainDetailsLoaded: state => state.domainDetailsFetchState === FetchState.Loaded,
        domainDetails: state => state.openSearchDomainDetails,
        userDocument: state => state.userSearchDocument,
        employerDocument: state => state.employerSearchDocument,
        osIndex: state => state.openSearchIndex,
        parameter: state => state.parameterValue
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setOpenSearchIndex (osIndex: OpenSearchIndex) {
            this.openSearchIndex = osIndex;
        },
        setDetails (response: OpenSearchPackagesResponse) {
          this.parameterValue = response.parameterValue;
        },
        setDomainDetailsFetchState (fetchState: FetchState) {
          this.domainDetailsFetchState = fetchState;
        },
        setUserSearchDocument (document: UserSearchDocument) {
          this.userSearchDocument = document;
        },
        setEmployerSearchDocument (document: EmployerSearchDocument) {
          this.employerSearchDocument = document;
        },
        setOpenSearchDomainDetails (details: OpenSearchDomainDetail) {
          this.openSearchDomainDetails = details;
        },
        getOpenSearchDomainDetails () {
          this.setDomainDetailsFetchState(FetchState.Loading);
          return UserService.getOpenSearchDomainDetails()
              .then(r => {
                  this.setOpenSearchDomainDetails(r.data);
                  this.setDomainDetailsFetchState(FetchState.Loaded);
              })
              .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        getOpenSearchIndexInfo () {
            this.setFetchState(FetchState.Loading);
            return UserService.getOpenSearchIndexInfo()
                .then(r => {
                    this.setOpenSearchIndex(r.data);
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        putOpenSearchIndexRequest (request: OpenSearchIndexRequest) {
            this.setFetchState(FetchState.Loading);
            return UserService.putOpenSearchIndexRequest(request)
                .then(r => {
                    this.setOpenSearchIndex(r.data);
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        getUserDocument (cognitoUsername: string) {
          return UserService.openSearchGetUserDocument(cognitoUsername)
          .then(document => {
            this.setUserSearchDocument(document.data);
          })
          .catch(err => {
            this.setUserSearchDocument(null);
            storeHelper.handleError({ error: err, rethrow: true });
          });
        },
        getEmployerDocument (employerId: number) {
          return UserService.openSearchGetEmployerDocument(employerId)
          .then(document => {
            this.setEmployerDocument(document.data);
          })
          .catch(err => {
            this.setEmployerDocument(null);
            storeHelper.handleError({ error: err, rethrow: true });
          });
        },
        reindexUser (cognitoUsername: string) {
          return UserService.openSearchReindexUser(cognitoUsername)
          .then(() => {
            const notifierStore = useNotifierStore();
            notifierStore.setSuccess(`Re-Index Request successfully submitted.`);
          })
          .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        reindexEmployer (employerId: number) {
          return UserService.openSearchReindexEmployer(employerId)
          .then(() => {
            const notifierStore = useNotifierStore();
            notifierStore.setSuccess(`Re-Index Request successfully submitted.`);
          })
          .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        uploadSynonymFile (txt: File) {
          return UserService.uploadSynonymFile(txt)
          .then(r => {
            this.setDetails(r.data);
            this.setFetchState(FetchState.Uninitialized);
          }).then(() => {
            const notifierStore = useNotifierStore();
            notifierStore.setSuccess(`Synonym Analyzer is in the ${this.parameter} state.`);
          })
          .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
    }
});